import type { FieldDef } from './fields'
import { Field, Input, Textarea } from '@/components/ui/Field'
import { MultiSelect } from '@/components/ui/MultiSelect'
import { Select, toOptions, type SelectOption } from '@/components/ui/Select'
import type { MasterData } from '@/hooks/useMasterData'
import { useMasterData } from '@/hooks/useMasterData'

/**
 * Renders one wizard field from its descriptor. Numbers are kept as strings while
 * being typed so a half-entered `-` or `0.` is not thrown away, and converted on blur.
 */
export function FieldControl({
  field,
  value,
  error,
  onChange,
  options,
}: {
  field: FieldDef
  value: unknown
  error?: string
  onChange: (value: unknown) => void
  options: ReturnType<typeof useMasterData>['options']
}) {
  const id = `field-${field.name}`

  const control = (() => {
    switch (field.kind) {
      case 'textarea':
        return (
          <Textarea
            id={id}
            rows={3}
            invalid={Boolean(error)}
            value={asText(value)}
            placeholder={field.placeholder}
            onChange={(event) => onChange(event.target.value)}
          />
        )

      case 'number':
        return (
          <Input
            id={id}
            inputMode="decimal"
            suffix={field.suffix}
            invalid={Boolean(error)}
            value={asText(value)}
            placeholder={field.placeholder}
            onChange={(event) => onChange(event.target.value)}
            onBlur={(event) => onChange(toNumber(event.target.value))}
          />
        )

      case 'date':
        return (
          <Input
            id={id}
            type="date"
            invalid={Boolean(error)}
            value={asText(value).slice(0, 10)}
            onChange={(event) => onChange(event.target.value || null)}
          />
        )

      case 'select':
        return (
          <Select
            id={id}
            invalid={Boolean(error)}
            value={value === null || value === undefined ? null : String(value)}
            onChange={(next) => onChange(next)}
            onClear={field.required ? undefined : () => onChange(null)}
            placeholder={field.required ? 'Select' : 'Not recorded'}
            options={optionsFor(field, options, value)}
          />
        )

      case 'multiselect':
        return (
          <MultiSelect
            id={id}
            invalid={Boolean(error)}
            value={Array.isArray(value) ? (value as string[]) : []}
            onChange={(next) => onChange(next)}
            options={optionsFor(field, options, value)}
          />
        )

      default:
        return (
          <Input
            id={id}
            invalid={Boolean(error)}
            suffix={field.suffix}
            value={asText(value)}
            placeholder={field.placeholder}
            onChange={(event) => onChange(event.target.value)}
          />
        )
    }
  })()

  return (
    <Field
      label={field.label}
      htmlFor={id}
      required={field.required}
      hint={field.hint}
      error={error}
      className={field.wide ? 'col-span-2' : undefined}
    >
      {control}
    </Field>
  )
}

const asText = (value: unknown): string =>
  value === null || value === undefined ? '' : String(value)

/** Empty stays empty; anything unparseable is left as typed for zod to reject. */
function toNumber(raw: string): number | string | null {
  const trimmed = raw.trim()
  if (trimmed === '') return null
  const parsed = Number(trimmed)
  return Number.isFinite(parsed) ? parsed : trimmed
}

export type { MasterData }

/**
 * A record can hold a name that has since been deactivated in Admin; it is kept
 * in the list so the control still shows what was recorded.
 */
function optionsFor(
  field: FieldDef,
  options: ReturnType<typeof useMasterData>['options'],
  value: unknown,
): SelectOption[] {
  if (field.choices) return toOptions(field.choices)
  if (field.choiceList) {
    const list = options[field.choiceList]
    return typeof value === 'string' && value && !list.some((option) => option.value === value)
      ? [...list, { value, label: value }]
      : list
  }
  return options[field.vocabulary!]
}
